import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Clock, 
  Play, 
  Stop, 
  Coffee, 
  MapPin,
  Timer,
  SpinnerGap,
  CheckCircle,
  Warning,
  ArrowRight,
  CaretDown,
  CaretUp,
  Pause
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { timeTrackingAPI } from '../api/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

const TimeClockWidget = ({ jobId, jobTitle, onStatusChange }) => {
  const [activeSession, setActiveSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [clockOutNotes, setClockOutNotes] = useState('');
  const [gpsStatus, setGpsStatus] = useState(null); // null, 'loading', 'success', 'error'
  const [currentLocation, setCurrentLocation] = useState(null);
  
  const timerRef = useRef(null);

  // Fetch active session
  const fetchActiveSession = useCallback(async () => {
    try {
      const response = await timeTrackingAPI.getActiveSession();
      if (response.data.success && response.data.active_session) {
        const session = response.data.active_session;
        // Only show if this is for our job
        if (session.job_id === jobId) {
          setActiveSession(session);
          setElapsedTime(session.work_minutes || 0);
        } else {
          setActiveSession(null);
        }
      } else {
        setActiveSession(null);
      }
    } catch (error) {
      console.error('Error fetching active session:', error);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchActiveSession();
  }, [fetchActiveSession]);

  // Timer for elapsed time
  useEffect(() => {
    if (activeSession && !activeSession.on_break) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 60000); // Update every minute
      
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [activeSession]);

  // Get GPS location
  const getLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      
      setGpsStatus('loading');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCurrentLocation(loc);
          setGpsStatus('success');
          resolve(loc);
        },
        (error) => {
          setGpsStatus('error');
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // Clock In
  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      // Try to get GPS location
      let location = null;
      try {
        location = await getLocation();
      } catch (gpsError) {
        console.log('GPS not available:', gpsError);
        // Continue without GPS
      }
      
      const response = await timeTrackingAPI.clockIn({
        job_id: jobId,
        latitude: location?.latitude,
        longitude: location?.longitude
      });
      
      if (response.data.success) {
        toast({ title: 'Clocked In!', description: `Started tracking time for ${jobTitle}` });
        setActiveSession(response.data.entry);
        setElapsedTime(0);
        onStatusChange?.('clocked_in');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clock in';
      toast({ title: 'Clock In Failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Clock Out
  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      let location = null;
      try {
        location = await getLocation();
      } catch (gpsError) {
        console.log('GPS not available:', gpsError);
      }
      
      const response = await timeTrackingAPI.clockOut(
        { notes: clockOutNotes || null },
        location?.latitude,
        location?.longitude
      );
      
      if (response.data.success) {
        toast({ 
          title: 'Clocked Out!', 
          description: `Total work time: ${response.data.summary.work_time}` 
        });
        setActiveSession(null);
        setElapsedTime(0);
        setShowNotesModal(false);
        setClockOutNotes('');
        onStatusChange?.('clocked_out');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clock out';
      toast({ title: 'Clock Out Failed', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Start Break
  const handleStartBreak = async (breakType = 'rest') => {
    setActionLoading(true);
    try {
      const response = await timeTrackingAPI.startBreak(breakType);
      if (response.data.success) {
        toast({ title: 'Break Started', description: `Taking a ${breakType} break` });
        fetchActiveSession();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start break';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // End Break
  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      const response = await timeTrackingAPI.endBreak();
      if (response.data.success) {
        toast({ title: 'Break Ended', description: `Back to work! Break was ${response.data.break_duration}` });
        fetchActiveSession();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to end break';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  // Format time display
  const formatTime = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Check if user is worker
  const userType = localStorage.getItem('userType');
  if (userType !== 'worker') {
    return null; // Only show for workers
  }

  if (loading) {
    return (
      <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl p-4 shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F]">
        <div className="flex items-center justify-center py-4">
          <SpinnerGap size={24} weight="bold" className="animate-spin text-[#2B4C6F]" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
      {/* Header */}
      <div 
        className={`p-4 cursor-pointer transition-colors ${
          activeSession 
            ? activeSession.on_break 
              ? 'bg-gradient-to-r from-amber-500 to-amber-600' 
              : 'bg-gradient-to-r from-emerald-500 to-emerald-600'
            : 'bg-gradient-to-r from-[#2B4C6F] to-blue-600'
        } text-white`}
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Clock size={24} weight="bold" />
            </div>
            <div>
              <h3 className="text-lg font-black">Time Clock</h3>
              <p className="text-sm text-white/80">
                {activeSession 
                  ? activeSession.on_break 
                    ? 'On Break' 
                    : 'Currently Tracking'
                  : 'Not Clocked In'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {activeSession && (
              <div className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold flex items-center gap-2">
                <Timer size={16} weight="bold" />
                {formatTime(elapsedTime)}
              </div>
            )}
            {expanded ? <CaretUp size={20} weight="bold" /> : <CaretDown size={20} weight="bold" />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="p-5">
          {activeSession ? (
            // Active Session View
            <div className="space-y-4">
              {/* Timer Display */}
              <div className="text-center py-4 bg-neutral-50 dark:bg-neutral-700 rounded-xl border-2 border-neutral-200 dark:border-neutral-600">
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">
                  {activeSession.on_break ? 'On Break Since' : 'Working Time'}
                </p>
                <p className="text-4xl font-black text-neutral-900 dark:text-white">
                  {formatTime(elapsedTime)}
                </p>
                {activeSession.break_minutes > 0 && (
                  <p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
                    <Coffee size={14} weight="bold" className="inline mr-1" />
                    {formatTime(activeSession.break_minutes)} total break time
                  </p>
                )}
              </div>

              {/* GPS Status */}
              {activeSession.gps_clock_in && (
                <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                  <MapPin size={16} weight="bold" />
                  <span>GPS verified at clock-in</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {activeSession.on_break ? (
                  <Button
                    onClick={handleEndBreak}
                    disabled={actionLoading}
                    className="col-span-2 h-14 bg-emerald-500 hover:bg-emerald-600 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none text-lg font-bold"
                    data-testid="end-break-btn"
                  >
                    {actionLoading ? (
                      <SpinnerGap size={24} className="animate-spin" />
                    ) : (
                      <>
                        <Play size={24} weight="bold" className="mr-2" />
                        End Break
                      </>
                    )}
                  </Button>
                ) : (
                  <>
                    <Button
                      onClick={() => handleStartBreak('rest')}
                      disabled={actionLoading}
                      variant="outline"
                      className="h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-bold"
                      data-testid="start-break-btn"
                    >
                      <Coffee size={20} weight="bold" className="mr-2" />
                      Break
                    </Button>
                    <Button
                      onClick={() => handleStartBreak('lunch')}
                      disabled={actionLoading}
                      variant="outline"
                      className="h-12 border-2 border-amber-500 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 font-bold"
                      data-testid="start-lunch-btn"
                    >
                      <Coffee size={20} weight="bold" className="mr-2" />
                      Lunch
                    </Button>
                  </>
                )}
                
                {!activeSession.on_break && (
                  <Button
                    onClick={() => setShowNotesModal(true)}
                    disabled={actionLoading}
                    className="col-span-2 h-14 bg-red-500 hover:bg-red-600 border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none text-lg font-bold"
                    data-testid="clock-out-btn"
                  >
                    {actionLoading ? (
                      <SpinnerGap size={24} className="animate-spin" />
                    ) : (
                      <>
                        <Stop size={24} weight="bold" className="mr-2" />
                        Clock Out
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            // Not Clocked In View
            <div className="space-y-4">
              <div className="text-center py-6 bg-neutral-50 dark:bg-neutral-700 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-600">
                <Clock size={48} weight="light" className="mx-auto text-neutral-300 dark:text-neutral-500 mb-2" />
                <p className="text-neutral-500 dark:text-neutral-400 mb-1">Ready to start work?</p>
                <p className="text-sm text-neutral-400 dark:text-neutral-500">
                  Clock in to track your hours
                </p>
              </div>

              {/* GPS Permission Notice */}
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <MapPin size={18} weight="bold" className="text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  GPS location will be recorded for verification
                </p>
              </div>

              <Button
                onClick={handleClockIn}
                disabled={actionLoading}
                className="w-full h-16 bg-emerald-500 hover:bg-emerald-600 border-2 border-black shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none text-xl font-black"
                data-testid="clock-in-btn"
              >
                {actionLoading ? (
                  <SpinnerGap size={28} className="animate-spin" />
                ) : (
                  <>
                    <Play size={28} weight="bold" className="mr-3" />
                    Clock In
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Clock Out Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-neutral-800 border-2 border-black shadow-[4px_4px_0px_#000]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Stop size={24} weight="bold" className="text-red-500" />
              Clock Out
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="p-4 bg-neutral-50 dark:bg-neutral-700 rounded-lg">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Work Time</p>
              <p className="text-2xl font-black text-neutral-900 dark:text-white">
                {formatTime(elapsedTime)}
              </p>
            </div>
            
            <div>
              <Label className="font-bold">Notes (Optional)</Label>
              <Textarea
                value={clockOutNotes}
                onChange={(e) => setClockOutNotes(e.target.value)}
                placeholder="Add any notes about today's work..."
                className="mt-1 min-h-[80px] border-2"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowNotesModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleClockOut}
              disabled={actionLoading}
              className="bg-red-500 hover:bg-red-600 border-2 border-black shadow-[2px_2px_0px_#000]"
            >
              {actionLoading ? (
                <SpinnerGap size={20} className="animate-spin" />
              ) : (
                <>
                  <CheckCircle size={20} weight="bold" className="mr-2" />
                  Confirm Clock Out
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TimeClockWidget;
